import React, {
	forwardRef,
	memo,
	type MouseEvent,
	type KeyboardEvent,
	useCallback,
	type PropsWithChildren,
} from 'react';
import noop from 'lodash/noop';
import { useFragment, graphql } from 'react-relay';
import { Box, xcss, Text } from '@atlaskit/primitives';
import { SingleLineTextInlineEditViewWithIsEditable } from '@atlassian/jira-issue-field-single-line-text-inline-edit-full/src/ui/single-line-text/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useIsEmbedMode } from '@atlassian/jira-issue-view-embed-mode/src/index.tsx';
import {
	useHoverPopoverContentRef,
	type HoverPopoverContentRef,
} from '@atlassian/jira-hover-popover/src/controllers/hover-popover-content-provider/index.tsx';
import { useGlobalObjectPreviewPanels } from '@atlassian/jira-preview-panels/src/PreviewPanelsProvider.tsx';
import { useShouldShowIframeIssuePreviewPanel } from '@atlassian/jira-preview-panels-rollout';
import {
	useIsDensityFull,
	useIsInlineEditingExtendedFieldSupportEnabled,
	useIsSingleLineRowHeightEnabled,
	useIsGridNavigationEnabled,
	useIsOpenIssueLinkEnabled,
} from '../../../../controllers/features/selectors.tsx';
import { useLazyLoadEditability } from '../../../../services/lazy-editability-loader/index.tsx';
import ErrorCell from '../../error-cell/index.tsx';
import { TableCellDiscreteLink } from '../../table-cell-link/index.tsx';
import { INLINE_EDIT_ANALYTICS_ATTRIBUTES } from '../common/inline-edit-analytics-attributes.tsx';
import { MAX_LINE_WRAP_NEW } from '../../../constants.tsx';
import { useTimelineCustomComponents } from '../../../../controllers/timeline-custom-components/index.tsx';
import type { IssueSummaryCell_issue$key } from './__generated__/IssueSummaryCell_issue.graphql';
import type { IssueSummaryCell$key } from './__generated__/IssueSummaryCell.graphql';
import { useUpdateIssueSummary } from './utils.tsx';
import { ActionContainer } from './action-container/index.tsx';
import { OpenIssueLink } from './open-issue-link/index.tsx';

export type Props = {
	fieldRef: IssueSummaryCell$key;
	issueRef: IssueSummaryCell_issue$key;
	isRowHoveredOrSelected?: boolean;
	/**
	 * Dispatched when a table cell with a link to the issue is selected. This method should return `true` to signal
	 * that the event has been handled, which will prevent default event handling.
	 */
	onIssueLinkSelected?: (issueKey: string) => boolean;
	readViewHighlightOnHover?: boolean;
	isMergedCellFocusedOrHovered?: boolean;
};

type OpenPreviewPanel = (props: {
	url: string;
	ari: string;
	name: string;
	iconUrl?: string | undefined;
}) => void;

export const IssueSummaryCell = memo<PropsWithChildren<Props>>(
	({
		fieldRef,
		issueRef,
		children,
		isRowHoveredOrSelected,
		onIssueLinkSelected,
		readViewHighlightOnHover,
		isMergedCellFocusedOrHovered,
	}: PropsWithChildren<Props>) => {
		const timelineCustomComponents = fg('jira_timeline_sticky_assignee_status_columns')
			? // eslint-disable-next-line react-hooks/rules-of-hooks
				useTimelineCustomComponents()
			: null;
		const SummaryCellContentComponent = timelineCustomComponents?.SummaryCellContent;
		const isEmbedMode = useIsEmbedMode();

		const data = useFragment<IssueSummaryCell$key>(
			graphql`
				fragment IssueSummaryCell on JiraSingleLineTextField
				@argumentDefinitions(
					isInlineEditingEnabled: { type: "Boolean!" }
					isEditableSyncLoaded: { type: "Boolean!" }
				) {
					id
					isEditableInIssueView @include(if: $isEditableSyncLoaded)
					lazyIsEditableInIssueView @include(if: $isInlineEditingEnabled)
					...singleLineText_issueFieldSingleLineTextInlineEditFull_SingleLineTextInlineEditViewWithIsEditable_fragmentRef
				}
			`,

			fieldRef,
		);

		const issueData = useFragment<IssueSummaryCell_issue$key>(
			graphql`
				fragment IssueSummaryCell_issue on JiraIssue
				@argumentDefinitions(
					isRemoteLinkedIssuesEnabled: { type: "Boolean!" }
					isIframePreviewPanelInSubtasksEnabled: {
						type: "Boolean!"
						provider: "@atlassian/jira-relay-provider/src/is-iframe-preview-panel-in-subtasks-enabled.relayprovider"
					}
				) {
					id @include(if: $isIframePreviewPanelInSubtasksEnabled)
					__id
					key
					isRemoteIssue @include(if: $isRemoteLinkedIssuesEnabled)
					webUrl @include(if: $isRemoteLinkedIssuesEnabled)
				}
			`,
			issueRef,
		);

		// eslint-disable-next-line react-hooks/rules-of-hooks
		const updateIssueSummary = fg('jsc_list_reparenting') ? useUpdateIssueSummary() : noop;

		const issueAriId = issueData?.id;

		let shouldShowIframePreviewPanel: () => boolean = useCallback(() => {
			return false;
		}, []);
		let openIframePreviewPanel: OpenPreviewPanel = useCallback(() => {
			/* empty */
		}, []);

		if (fg('issue_preview_panel_issue_view_subtask')) {
			// eslint-disable-next-line react-hooks/rules-of-hooks
			const shouldShowIssueInIframePreviewPanel = useShouldShowIframeIssuePreviewPanel();
			shouldShowIframePreviewPanel = shouldShowIssueInIframePreviewPanel;
		}

		if (shouldShowIframePreviewPanel()) {
			// eslint-disable-next-line react-hooks/rules-of-hooks
			const { openPreviewPanel } = useGlobalObjectPreviewPanels();
			openIframePreviewPanel = openPreviewPanel;
		}

		const isOpenIssueLinkEnabled = fg('jira_issue_table_open_issue_link_config')
			? // eslint-disable-next-line react-hooks/rules-of-hooks
				useIsOpenIssueLinkEnabled()
			: true;

		const onSubmitSucceeded = useCallback(
			(value: string) => {
				if (issueData?.__id && fg('jsc_list_reparenting')) {
					updateIssueSummary(issueData.__id, value);
				}
			},
			[issueData?.__id, updateIssueSummary],
		);

		// If the property does not exist in fragment data then we know it has been conditionally excluded because inline
		// editing is disabled. In this case we explicitly set isEditable to false to bypass the lazy editability query.
		let isEditable: boolean | null | undefined = false;
		if ('isEditableInIssueView' in data) {
			isEditable = data.isEditableInIssueView;
		} else if ('lazyIsEditableInIssueView' in data) {
			isEditable = data.lazyIsEditableInIssueView;
		}

		useLazyLoadEditability(data.id, isEditable);

		const isDensityCompact = !useIsDensityFull();
		const isInlineEditingExtendedFieldSupportEnabled =
			useIsInlineEditingExtendedFieldSupportEnabled();

		const isSingleLineRowHeightEnabled = useIsSingleLineRowHeightEnabled();

		const issueUrl =
			issueData?.isRemoteIssue && issueData?.webUrl && fg('jira-linked-work-items-targeting-gate')
				? issueData.webUrl
				: `/browse/${issueData.key}`;

		const onLinkClick = useCallback(
			(e: MouseEvent | KeyboardEvent) => {
				if (
					issueData?.isRemoteIssue &&
					issueData?.webUrl &&
					fg('jira-linked-work-items-targeting-gate')
				) {
					return;
				}

				// Use default browser event handling when a modifier key is pressed during the click event
				const isModifierKeyUsed = e.ctrlKey || e.shiftKey || e.metaKey;

				if (shouldShowIframePreviewPanel() && !isModifierKeyUsed) {
					e.preventDefault();

					openIframePreviewPanel({
						ari: issueAriId || '',
						url: issueUrl,
						name: 'jira-issue',
					});
					return;
				}

				const handled =
					!isEmbedMode &&
					!isModifierKeyUsed &&
					onIssueLinkSelected !== undefined &&
					onIssueLinkSelected(issueData.key);
				if (handled) {
					e.preventDefault();
				}
			},
			[
				issueData?.isRemoteIssue,
				issueData?.webUrl,
				issueData.key,
				shouldShowIframePreviewPanel,
				isEmbedMode,
				onIssueLinkSelected,
				openIframePreviewPanel,
				issueAriId,
				issueUrl,
			],
		);

		const isGridNavigationEnabled = useIsGridNavigationEnabled();
		let showOpenIssueLink = isOpenIssueLinkEnabled;
		if (
			// eslint-disable-next-line jira/ff/no-preconditioning
			isGridNavigationEnabled &&
			fg('jira_list_merged_column_gate') &&
			fg('jira_list_merged_column_inline_create_icon_style')
		) {
			if (fg('jira_list_merged_cell_action_buttons_visibility')) {
				showOpenIssueLink = isOpenIssueLinkEnabled && !!isMergedCellFocusedOrHovered;
			} else {
				showOpenIssueLink = isOpenIssueLinkEnabled && !!isRowHoveredOrSelected;
			}
		}

		const hoverPopoverRefFactory = isSingleLineRowHeightEnabled
			? // eslint-disable-next-line react-hooks/rules-of-hooks
				useHoverPopoverContentRef(data.id)
			: null;

		const renderCustomReadView = useCallback(
			(text: string | null) => {
				if (!text) {
					return <ErrorCell cellType="summary" reason="Missing required fields" />;
				}

				let hoverPopoverRef: HoverPopoverContentRef = null;
				if (hoverPopoverRefFactory) {
					hoverPopoverRef = hoverPopoverRefFactory({
						content: text,
						shouldRenderToContent: true,
					});
				}

				const content = (
					<TableCellDiscreteLink
						innerFocusOutline
						href={issueUrl}
						isSubtle={false}
						onClick={onLinkClick}
						data-testid="native-issue-table.common.ui.issue-cells.issue-summary.issue-summary-cell"
						{...(isEmbedMode && { target: '_blank', rel: 'noopener' })}
					>
						{text}
					</TableCellDiscreteLink>
				);

				if (isInlineEditingExtendedFieldSupportEnabled && isSingleLineRowHeightEnabled) {
					return (
						<>
							<Text
								ref={hoverPopoverRef}
								testId="native-issue-table.common.ui.issue-cells.issue-summary.issue-summary-cell"
								maxLines={MAX_LINE_WRAP_NEW}
							>
								{text}
							</Text>
							{fg('jira_list_merged_column_inline_create_icon_style') ? (
								<Box xcss={actionButtonsWrapStyles}>
									{showOpenIssueLink && (
										<OpenIssueLink issueKey={issueData.key} url={issueUrl} onClick={onLinkClick} />
									)}
									{fg('jira_list_merged_column_gate') && children}
								</Box>
							) : (
								<>
									{showOpenIssueLink && (
										<OpenIssueLink issueKey={issueData.key} url={issueUrl} onClick={onLinkClick} />
									)}
									{fg('jira_list_merged_column_gate') && children}
								</>
							)}
						</>
					);
				}

				if (isInlineEditingExtendedFieldSupportEnabled) {
					return (
						<>
							<Text
								testId="native-issue-table.common.ui.issue-cells.issue-summary.issue-summary-cell"
								maxLines={isDensityCompact ? 2 : undefined}
							>
								{text}
							</Text>
							{fg('jira_list_merged_column_inline_create_icon_style') ? (
								<Box xcss={actionButtonsWrapStyles}>
									{showOpenIssueLink && (
										<OpenIssueLink issueKey={issueData.key} url={issueUrl} onClick={onLinkClick} />
									)}
									{fg('jira_list_merged_column_gate') && children}
								</Box>
							) : (
								<>
									{showOpenIssueLink && (
										<OpenIssueLink issueKey={issueData.key} url={issueUrl} onClick={onLinkClick} />
									)}
									{fg('jira_list_merged_column_gate') && children}
								</>
							)}
						</>
					);
				}

				// Span is necessary here to ensure only link text is clickable for multiline summaries, rather than the
				// entire content box.
				return isDensityCompact ? (
					<Box as="span" xcss={textOverflowStyles}>
						{content}
					</Box>
				) : (
					<span>{content}</span>
				);
			},

			// eslint-disable-next-line react-hooks/exhaustive-deps
			fg('jira_timeline_harmonisation_m1_issue_table_updates')
				? [
						children,
						isDensityCompact,
						isEmbedMode,
						isInlineEditingExtendedFieldSupportEnabled,
						isSingleLineRowHeightEnabled,
						issueData.key,
						onLinkClick,
						showOpenIssueLink,
						hoverPopoverRefFactory,
						SummaryCellContentComponent,
					]
				: [
						children,
						isDensityCompact,
						isEmbedMode,
						isInlineEditingExtendedFieldSupportEnabled,
						isSingleLineRowHeightEnabled,
						issueData.key,
						onLinkClick,
						showOpenIssueLink,
						hoverPopoverRefFactory,
					],
		);

		let node = null;

		if (!fg('jira_timeline_sticky_assignee_status_columns')) {
			node = (
				<SingleLineTextInlineEditViewWithIsEditable
					attributes={INLINE_EDIT_ANALYTICS_ATTRIBUTES}
					spacing="default"
					editViewPopup
					editViewPopupAlignBlock="center"
					// @ts-expect-error: to unblock React 18.2.0 -> 18.3.1 version bump
					editViewPopupTarget={EditViewPopupTarget}
					fragmentRef={data}
					isEditable={isEditable ?? false}
					isRequired
					maxLength={255}
					readViewFitContainerHeight
					renderCustomReadView={renderCustomReadView}
					hideEditButton={isInlineEditingExtendedFieldSupportEnabled}
					onSubmitSucceeded={fg('jsc_list_reparenting') ? onSubmitSucceeded : undefined}
					hasReadViewTruncateDisabled={isSingleLineRowHeightEnabled}
					readViewHighlightOnHover={readViewHighlightOnHover}
				/>
			);
		} else {
			const commonProps = {
				attributes: INLINE_EDIT_ANALYTICS_ATTRIBUTES,
				spacing: 'default',
				editViewPopup: true,
				editViewPopupAlignBlock: 'center',
				fragmentRef: data,
				isRequired: true,
				maxLength: 255,
				readViewFitContainerHeight: true,
				renderCustomReadView,
				hideEditButton: isInlineEditingExtendedFieldSupportEnabled,
				onSubmitSucceeded: fg('jsc_list_reparenting') ? onSubmitSucceeded : undefined,
				hasReadViewTruncateDisabled: isSingleLineRowHeightEnabled,
				readViewHighlightOnHover,
				isEditable: isEditable ?? false,
			};
			if (SummaryCellContentComponent) {
				node = (
					<SummaryCellContentComponent
						{...commonProps}
						// @ts-expect-error: to unblock React 18.2.0 -> 18.3.1 version bump
						editViewPopupTarget={EditViewPopupTarget}
						isMergedCellFocusedOrHovered={isMergedCellFocusedOrHovered}
						issueKey={issueData.key}
						onLinkClick={onLinkClick}
						hoverPopoverRefFactory={hoverPopoverRefFactory}
					>
						{children}
					</SummaryCellContentComponent>
				);
			} else {
				node = (
					<SingleLineTextInlineEditViewWithIsEditable
						{...commonProps}
						// @ts-expect-error: to unblock React 18.2.0 -> 18.3.1 version bump
						editViewPopupTarget={EditViewPopupTarget}
					/>
				);
			}
		}

		if (isInlineEditingExtendedFieldSupportEnabled) {
			return <ActionContainer>{node}</ActionContainer>;
		}
		return node;
	},
);

const EditViewPopupTarget = forwardRef<HTMLDivElement, {}>((_, ref) => {
	return <Box xcss={customTargetStyles} ref={ref} />;
});

// We have an extra flex container around our inline edit component so we can show the open issue link button. We need
// to explicitly center align the edit view popup target to ensure consistent alignment in the table cell.
const customTargetStyles = xcss({
	alignSelf: 'center',
	width: '0',
});

const textOverflowStyles = xcss({
	display: '-webkit-box',
	WebkitBoxOrient: 'vertical',
	WebkitLineClamp: 2,
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	wordBreak: 'break-word',
});

const actionButtonsWrapStyles = xcss({
	display: 'flex',
	gap: 'space.050',
});
